import { ChangeDetectionStrategy, Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { GatewayInstance } from '@vsolv/packages/payments/domain';
import { PaymentGatewayInstanceService } from '@vsolv/packages/payments/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { IconButtonColumn, TextColumn } from '@vsolv/vectors/table';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-gateway-routing-rules',
  templateUrl: './gateway-routing-rules.component.html',
  standalone: false,
})
export class GatewayRoutingRulesComponent implements OnInit {
  constructor(
    private readonly fb: FormBuilder,
    private readonly gateways: PaymentGatewayInstanceService,
    private readonly toast: ToastService
  ) {}

  @Input() gatewayInstances: GatewayInstance.Model[] = [];
  @Input() canManage = false;

  @ViewChild('ruleDialog') private ruleDialog?: DialogComponent;
  @ViewChild('deleteDialog') private deleteDialog?: DialogComponent;

  readonly scope = GatewayInstance.RoutingRuleScopeType;
  readonly form = this.fb.nonNullable.group({
    scopeType: GatewayInstance.RoutingRuleScopeType.POLICY,
    policyId: '',
    distributorId: '',
    gatewayInstanceId: '',
    enabled: true,
  });

  readonly columns = [
    new TextColumn<GatewayInstance.RoutingRuleModel>({ header: 'Status', fitContent: true }, rule => ({
      text: rule.enabled ? 'Enabled' : 'Disabled',
    })),
    new TextColumn<GatewayInstance.RoutingRuleModel>({ header: 'Priority', fitContent: true }, rule => ({
      text: this.priorityLabel(rule.scopeType),
      classes: 'font-medium text-gray-900 whitespace-nowrap',
    })),
    new TextColumn<GatewayInstance.RoutingRuleModel>({ header: 'Applies to' }, rule => ({
      text: this.criteriaLabel(rule),
      classes: 'text-gray-900',
    })),
    new TextColumn<GatewayInstance.RoutingRuleModel>({ header: 'Routes to' }, rule => ({
      text: this.gatewayName(rule.gatewayInstanceId),
      classes: 'font-medium text-gray-900 whitespace-nowrap',
    })),
    new IconButtonColumn<GatewayInstance.RoutingRuleModel>({ stickyEnd: true, fitContent: true }, rule => ({
      type: 'clear',
      rounded: true,
      icon: 'edit-01',
      tooltipText: `Edit ${this.scopeLabel(rule.scopeType).toLowerCase()} rule`,
      click: () => this.openEdit(rule),
    })),
    new IconButtonColumn<GatewayInstance.RoutingRuleModel>({ stickyEnd: true, fitContent: true }, rule => ({
      type: 'clear',
      rounded: true,
      theme: 'danger',
      icon: 'trash-01',
      tooltipText: 'Delete routing rule',
      click: () => this.openDelete(rule),
    })),
  ];

  rules: GatewayInstance.RoutingRuleModel[] = [];
  policies: GatewayInstance.RoutingRulePolicyOption[] = [];
  distributors: GatewayInstance.RoutingRuleDistributorOption[] = [];
  editingId: string | null = null;
  selectedRule: GatewayInstance.RoutingRuleModel | null = null;
  loading = false;
  saving = false;
  deleting = false;
  error: string | null = null;

  get activeGateways(): GatewayInstance.Model[] {
    return this.gatewayInstances.filter(gateway => gateway.status === GatewayInstance.Status.ACTIVE);
  }

  get defaultGateway(): GatewayInstance.Model | null {
    return this.gatewayInstances.find(gateway => gateway.isDefault) ?? null;
  }

  get selectableGateways(): GatewayInstance.Model[] {
    return this.gatewayInstances.filter(
      gateway =>
        gateway.status === GatewayInstance.Status.ACTIVE ||
        (!this.form.controls.enabled.value &&
          this.editingId !== null &&
          gateway.id === this.form.controls.gatewayInstanceId.value)
    );
  }

  get needsPolicy(): boolean {
    return this.form.controls.scopeType.value !== this.scope.DISTRIBUTOR;
  }

  get needsDistributor(): boolean {
    return this.form.controls.scopeType.value !== this.scope.POLICY;
  }

  get canSave(): boolean {
    return (
      this.canManage &&
      !this.saving &&
      (!this.needsPolicy || !!this.form.controls.policyId.value) &&
      (!this.needsDistributor || !!this.form.controls.distributorId.value) &&
      this.gatewayInstances.some(
        gateway =>
          gateway.id === this.form.controls.gatewayInstanceId.value &&
          (!this.form.controls.enabled.value || gateway.status === GatewayInstance.Status.ACTIVE)
      )
    );
  }

  ngOnInit(): void {
    void this.refresh();
  }

  async refresh(): Promise<void> {
    if (!this.canManage) return;
    this.loading = true;
    this.error = null;
    try {
      const [rules, options] = await Promise.all([
        this.gateways.listRoutingRules(),
        this.gateways.listRoutingRuleOptions(),
      ]);
      this.rules = this.sortByPriority(rules);
      this.policies = options.policies;
      this.distributors = options.distributors;
    } catch (error: unknown) {
      this.error = this.errorMessage(error, 'Unable to load routing rules.');
    } finally {
      this.loading = false;
    }
  }

  openCreate(): void {
    if (!this.canManage) return;
    this.editingId = null;
    this.form.reset({
      scopeType: this.scope.POLICY,
      policyId: '',
      distributorId: '',
      gatewayInstanceId: '',
      enabled: true,
    });
    this.ruleDialog?.open();
  }

  openEdit(rule: GatewayInstance.RoutingRuleModel): void {
    if (!this.canManage) return;
    this.editingId = rule.id;
    this.form.reset({
      scopeType: rule.scopeType,
      policyId: rule.policyId ?? '',
      distributorId: rule.distributorId ?? '',
      gatewayInstanceId: rule.gatewayInstanceId,
      enabled: rule.enabled,
    });
    this.ruleDialog?.open();
  }

  closeEditor(): void {
    if (!this.saving) this.ruleDialog?.close();
  }

  openDelete(rule: GatewayInstance.RoutingRuleModel): void {
    if (!this.canManage) return;
    this.selectedRule = rule;
    this.deleteDialog?.open();
  }

  closeDelete(): void {
    if (this.deleting) return;
    this.deleteDialog?.close();
    this.selectedRule = null;
  }

  async save(): Promise<void> {
    if (!this.canSave) return;
    const value = this.form.getRawValue();
    const request: GatewayInstance.RoutingRuleRequest = {
      scopeType: value.scopeType,
      policyId: this.needsPolicy ? value.policyId : null,
      distributorId: this.needsDistributor ? value.distributorId : null,
      gatewayInstanceId: value.gatewayInstanceId,
      enabled: value.enabled,
    };
    this.saving = true;
    try {
      if (this.editingId) await this.gateways.updateRoutingRule(this.editingId, request);
      else await this.gateways.createRoutingRule(request);
      this.toast.show({
        type: 'success',
        title: 'Routing rule saved',
        text: value.enabled
          ? 'New sales will use this rule when its scope matches.'
          : 'New sales will skip this disabled rule.',
      });
      this.ruleDialog?.close();
      await this.refresh();
    } catch (error: unknown) {
      this.toast.show({
        type: 'error',
        title: 'Unable to save rule',
        text: this.errorMessage(error, 'Check the rule and try again.'),
      });
    } finally {
      this.saving = false;
    }
  }

  async deleteRule(): Promise<void> {
    if (!this.canManage) return;
    if (!this.selectedRule || this.deleting) return;
    this.deleting = true;
    try {
      await this.gateways.deleteRoutingRule(this.selectedRule.id);
      this.toast.show({
        type: 'success',
        title: 'Routing rule deleted',
        text: 'New sales will use the next matching rule or the tenant default.',
      });
      this.deleteDialog?.close();
      this.selectedRule = null;
      await this.refresh();
    } catch (error: unknown) {
      this.toast.show({ type: 'error', title: 'Unable to delete rule', text: this.errorMessage(error, 'Try again.') });
    } finally {
      this.deleting = false;
    }
  }

  scopeLabel(scopeType: GatewayInstance.RoutingRuleScopeType): string {
    if (scopeType === this.scope.POLICY_DISTRIBUTOR) return 'Policy + distributor';
    return scopeType === this.scope.DISTRIBUTOR ? 'Distributor' : 'Policy';
  }

  priorityLabel(scopeType: GatewayInstance.RoutingRuleScopeType): string {
    const priority = scopeType === this.scope.POLICY_DISTRIBUTOR ? 1 : scopeType === this.scope.DISTRIBUTOR ? 2 : 3;
    return `${priority}. ${this.scopeLabel(scopeType)}`;
  }

  criteriaLabel(rule: GatewayInstance.RoutingRuleModel): string {
    const labels = [
      rule.policyId ? this.policyName(rule.policyId) : null,
      rule.distributorId ? this.distributorName(rule.distributorId) : null,
    ];
    return labels.filter((label): label is string => !!label).join(' + ');
  }

  policyLabel(policy: GatewayInstance.RoutingRulePolicyOption): string {
    return policy.title;
  }

  policyName(id: string): string {
    const policy = this.policies.find(item => item.id === id);
    return policy ? this.policyLabel(policy) : id;
  }

  distributorName(id: string): string {
    return this.distributors.find(distributor => distributor.id === id)?.name ?? id;
  }

  gatewayName(id: string): string {
    return this.gatewayInstances.find(gateway => gateway.id === id)?.name ?? id;
  }

  private sortByPriority(rules: GatewayInstance.RoutingRuleModel[]): GatewayInstance.RoutingRuleModel[] {
    const priority = {
      [this.scope.POLICY_DISTRIBUTOR]: 1,
      [this.scope.DISTRIBUTOR]: 2,
      [this.scope.POLICY]: 3,
    };
    return [...rules].sort((a, b) => priority[a.scopeType] - priority[b.scopeType]);
  }

  private errorMessage(error: unknown, fallback: string): string {
    const message = (error as { error?: { message?: string | string[] } })?.error?.message;
    return Array.isArray(message) ? message.join(', ') : (message ?? fallback);
  }
}
